import React, { useEffect, useState, useRef } from "react";
import { Button, Card, Col, Form, Input, Row, Select, SelectProps } from "antd";
import { useParams } from "react-router-dom";
import { Toast } from 'primereact/toast';
import HTTPService from "../../../utils/makerequest";
import { ICompanyData } from "../../../interfaces/ICompanyData";
import { IAuthentication, UserRole } from "../../../interfaces/authentication";
import { StateLoader } from "../../../redux/StateLoader";
import { IBrandData } from "../../../interfaces/IBrandData";
import { useTranslation } from 'react-i18next';
import { IPageProps } from "../../../interfaces/page-data";
import { SyncOutlined, CloseOutlined } from "@ant-design/icons";

const UpdateBrand: React.FunctionComponent<IPageProps> = () => {
  const { brandId } = useParams<any>();
  const toast = useRef<any>(null);
  const [brandData, setBrandData] = useState<IBrandData>();
  const [companyData, setCompanyData] = useState<ICompanyData[]>();
  const loader = new StateLoader();
  const userInfo: IAuthentication = loader.loadAuthState();
  const { t } = useTranslation();

  useEffect(() => {
    fetchData();
    if (brandId) {
      getBrandData(brandId);
    }
  }, [brandId]);

  useEffect(() => {
    if (brandId != null) {
      getBrandData(brandId);
    } else if (brandData?.brandId) {
      getBrandData(brandData.brandId);
    }
  }, [brandId, brandData?.brandId]);

  const filterOption: SelectProps<any>['filterOption'] = (input, option) => {
    return (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase());
  };


  const fetchData = async () => {
    const result = await HTTPService.GETCompanyNames();
    setCompanyData(result);
  };

  const getBrandData = (brandId: number) => {
    HTTPService.GET(`/Brand/ById?brandId=${brandId}`)
      .then((res) => {
        if (res.status === 200 && res.data) {
          setBrandData(res.data);
        } else {
          toast.current?.show({
            severity: 'error',
            summary: t('mistake'),
            detail: res.error?.message || t('error_fetching_data'),
            life: 3000
          });
        }
      })
      .catch((error) => {
        toast.current?.show({
          severity: 'error',
          summary: t('mistake'),
          detail: t('error_fetching_data'),
          life: 3000
        });
        console.error("Error fetching brand data:", error);
      });
  };

  const updateBrand = (formData) => {
    const updatedBrand = {
      brandId: brandId,
      brandName: formData.brandName,
      companyId: formData.companyId || userInfo.companyId,
    };

    HTTPService.PUT(`/Brand/updateBrand`, updatedBrand)
      .then((res) => {
        if (res.status === 200) {
          toast.current?.show({
            severity: 'success',
            summary: t('successful'),
            detail: t('brand_updated_successfully'),
            life: 3000
          });
          window.location.replace("/admin/brand-management");
        } else if (res.status === 450 || res.status === 400) {
          toast.current?.show({
            severity: 'error',
            summary: t('mistake'),
            detail: t(res.data),
            life: 3000
          });
        } else {
          toast.current?.show({
            severity: 'error',
            summary: t('mistake'),
            detail: t('brand_update_failed'),
            life: 3000
          });
        }
      })
      .catch(() => {
        toast.current?.show({
          severity: 'error',
          summary: t('mistake'),
          detail: t('brand_update_failed'),
          life: 3000
        });
      });
  };

  const handleCancel = () => {
    window.location.replace("/admin/brand-management/");
  };

  return (
    <>
      <Toast ref={toast} />
      <Form
        layout="vertical"
        onFinish={updateBrand}
        fields={[
          { name: ["brandName"], value: brandData?.brandName },
          { name: ["brandId"], value: brandId || brandData?.brandId },
          { name: ["companyId"], value: brandData?.companyId },
        ]}
      >
        <Card style={{ padding: "50px" }}>
          <h3>{t('update_brand')}</h3>
          {userInfo?.role === UserRole.AltisAdmin ? (
            <Form.Item
              name='companyId'
              label={t('Select company')}
              rules={[
                {
                  required: true,
                  message: t('please_select_a_company'),
                },
              ]}
            >
              <Select showSearch placeholder={t('company_name')} style={{ marginBottom: '6px' }} filterOption={filterOption}>
                {companyData?.map((company) => (
                  <Select.Option key={company.companyId} value={company.companyId}>
                    {company.companyName}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          ) : null}
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="brandName"
                label={t('brand_name')}
                rules={[
                  {
                    required: true,
                    message: t('please_input_your_brand_name'),
                    whitespace: true,
                  },
                ]}
              >
                <Input style={{ borderRadius: 0 }} placeholder={t('brand_name')} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Row gutter={[4, 4]} justify="end" align="middle">
                <Col xs={12} sm={4}>
                    <Button
                        block
                        onClick={handleCancel}
                        type="primary"
                        style={{
                            height: 64,
                            backgroundColor: 'transparent',
                            color: '#1890ff',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '0 10px',
                            border: 'none',
                            transition: 'all 0.3s ease',
                            cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.05)';
                            e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.2)';
                            e.currentTarget.style.color = '#40a9ff';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.boxShadow = 'none';
                            e.currentTarget.style.color = '#1890ff';
                        }}
                    >
                        <CloseOutlined style={{ fontSize: 20 }} />
                        <span style={{ fontSize: '12px', marginTop: '8px', textAlign: 'center' }}>
                            {t('cancel')}
                        </span>
                    </Button>
                </Col>
                <Col xs={12} sm={4}>
                    <Button
                        block
                        htmlType="submit"
                        type="primary"
                        style={{
                            height: 64,
                            backgroundColor: 'transparent',
                            color: '#1890ff',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '0 10px',
                            border: 'none',
                            transition: 'all 0.3s ease',
                            cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.05)';
                            e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.2)';
                            e.currentTarget.style.color = '#40a9ff';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.boxShadow = 'none';
                            e.currentTarget.style.color = '#1890ff';
                        }}
                    >
                        <SyncOutlined style={{ fontSize: 20 }} />
                        <span style={{ fontSize: '12px', marginTop: '8px', textAlign: 'center' }}>
                            {t('update')}
                        </span>
                    </Button>
                </Col>
            </Row>
          </Form.Item>
        </Card>
      </Form>
    </>
  );
};

export default UpdateBrand;
